import * as React from "react";

import { Check } from "./check";
import { cn } from "./cn";

/*
 * A numbered list of steps: the service-tint numeral disc beside a title and
 * an optional body. Replaces the hand-rolled numbered circles the screens
 * each drew for themselves. A finished step swaps its numeral for the check
 * disc; the current one gets the solid service disc.
 */

type StepState = "default" | "current" | "done";

type StepListProps = React.ComponentProps<"ol">;

function StepList({ className, children, ...props }: StepListProps) {
  let index = 0;
  const numbered = React.Children.map(children, (child) => {
    if (!React.isValidElement<StepListItemProps>(child)) return child;
    index += 1;
    return child.props.number === undefined
      ? React.cloneElement(child, { number: index })
      : child;
  });
  return (
    <ol
      data-slot="step-list"
      className={cn("flex flex-col gap-4", className)}
      {...props}
    >
      {numbered}
    </ol>
  );
}

type StepListItemProps = Omit<React.ComponentProps<"li">, "title"> & {
  title: React.ReactNode;
  /** The numeral; StepList fills it from the item's position when omitted. */
  number?: number;
  state?: StepState;
};

const markerTone: Record<StepState, string> = {
  default: "bg-service-tint text-service",
  current: "bg-service text-card",
  done: "",
};

function StepListItem({
  title,
  number,
  state = "default",
  className,
  children,
  ...props
}: StepListItemProps) {
  return (
    <li
      data-slot="step-list-item"
      data-state={state}
      className={cn("flex items-start gap-3", className)}
      {...props}
    >
      {state === "done" ? (
        <Check size={24} className="shrink-0" />
      ) : (
        <span
          data-slot="step-list-marker"
          aria-hidden="true"
          className={cn(
            "flex size-6 shrink-0 items-center justify-center rounded-pill text-xs font-medium tabular-nums",
            markerTone[state],
          )}
        >
          {number}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-0.5">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {children ? <div className="text-sm text-body">{children}</div> : null}
      </div>
    </li>
  );
}

export { StepList, StepListItem };
export type { StepListItemProps, StepListProps, StepState };
